import {
  type APIFieldAttributes,
  type APITagAttributes,
  ArdoqId,
  GroupingRule,
  GroupType,
} from '@ardoq/api-types';
import { PerspectivesGroupsOptions } from '@ardoq/perspectives';
import { action$, derivedStream, ofType, spreadMap } from '@ardoq/rxbeach';
import { combineLatest, from, merge } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { workspaceInterface } from '@ardoq/workspace-interface';
import { groupingRuleInterface } from 'modelInterface/groupingRules/groupingRuleInterface';
import { fieldInterface } from '@ardoq/field-interface';
import { tagInterface } from '@ardoq/tag-interface';
import { perspectiveInterface } from 'modelInterface/perspectives/perspectiveInterface';
import { context$ } from '../context/context$';
import { fields$ } from '../fields/fields$';
import {
  editorOpened,
  notifyPerspectiveApplied,
} from '../../perspective/actions';
import { tags$ } from '../tags/tags$';
import { groupBy, isEqual, uniq } from 'lodash';
import { isTruthy } from '@ardoq/common-helpers';
import { mapFetchedOptionsToGroupOptions } from './fetchSavedPerspectiveEntriesFromClosedWorkspaces';
import { setActivePerspective } from '../filters/FilterActions';
import { groupingRules$ } from '../grouping/groupingRules$';
import { mapToPerspectiveGroupingRule } from './mapToPerspectiveGroupingRule';
import {
  getComponentTypeOptionsPerWorkspace,
  getReferenceTypeOptionsPerWorkspace,
  getWorkspaceOptions,
  groupTagOptionsPerWorkspace,
  toFieldOptions,
  type VirtualReferenceTypesByWorkspace,
} from './groupingOptionsPerWorkspace';
import { handleError, tagApi } from '@ardoq/api';
import {
  viewpointOptionsSourceData$,
  ViewpointOptionsSourceData,
} from './viewpointOptionsSourceData$';
import {
  endTransaction,
  runWithSpan,
  startTransaction,
  Team,
} from '@ardoq/profiling';
import { workspaceAndModel$ } from 'streams/workspaceAndModel$';
import {
  enhancedScopeDataOperations,
  scopeDataOperations,
  type WorkspaceWithTypes,
} from '@ardoq/scope-data';
import { loadedGraph$ } from 'traversals/loadedGraph$';
import {
  ContextShape,
  type LoadedGraphWithViewpointMode,
  WorkspaceAndModelLookup,
} from '@ardoq/data-model';

export const getVirtualReferenceTypesByWorkspaceFromLoadedGraph = (
  loadedGraph: LoadedGraphWithViewpointMode
): VirtualReferenceTypesByWorkspace =>
  loadedGraph.isViewpointMode
    ? enhancedScopeDataOperations.getVirtualReferenceTypesByWorkspace(
        loadedGraph.scopeData
      )
    : {};

/**
 * We're interested only in component fields as grouping is made only for components.
 * There's no point offering user grouping by a custom field that is available only for references.
 */
const getComponentFieldOptionsPerWorkspace = (
  workspacesWithTypes: WorkspaceWithTypes[],
  availableComponentFields?: Set<string>
) => {
  const result: Record<string, { id: string; name: string }[]> = {};

  const allFields = fieldInterface.getAllFields();
  const fieldsByWorkspaceId = groupBy(allFields, field =>
    workspaceInterface.getWorkspaceIdByModelId(field.model)
  );

  for (const { workspace } of workspacesWithTypes) {
    const fields = fieldsByWorkspaceId[workspace._id] ?? [];
    const filteredFields = availableComponentFields
      ? fields.filter(
          field =>
            availableComponentFields.has(field.name) &&
            (field.global || field.componentType.length)
        )
      : fields.filter(field => field.global || field.componentType.length);

    result[workspace._id] = toFieldOptions(filteredFields);
  }

  return result;
};

const getTagOptionsPerWorkspace = (
  workspacesWithTypes: WorkspaceWithTypes[]
) => {
  return workspacesWithTypes.reduce((previousValue, { workspace }) => {
    const tags = tagInterface.getWorkspaceTags(workspace._id);
    const tagOptions = tags.map(tag => ({
      id: tag._id,
      name: tag.name,
    }));

    return {
      ...previousValue,
      [workspace._id]: tagOptions,
    };
  }, {});
};

export const toGroupingOptions = (
  workspacesWithTypes: WorkspaceWithTypes[],
  virtualReferenceTypesByWorkspace: VirtualReferenceTypesByWorkspace
): PerspectivesGroupsOptions => {
  return {
    appliedGroupingRules: groupingRuleInterface
      .getAll()
      .map(mapToPerspectiveGroupingRule),
    workspaces: getWorkspaceOptions(workspacesWithTypes),
    componentTypesPerWorkspace:
      getComponentTypeOptionsPerWorkspace(workspacesWithTypes),
    referenceTypesPerWorkspace: getReferenceTypeOptionsPerWorkspace(
      workspacesWithTypes,
      virtualReferenceTypesByWorkspace
    ),
    fieldsPerWorkspace:
      getComponentFieldOptionsPerWorkspace(workspacesWithTypes),
    tagsPerWorkspace: getTagOptionsPerWorkspace(workspacesWithTypes),
    globalComponentTypeNames: [],
    globalReferenceTypeNames: [],
  };
};

type ToGroupingOptionsForTraversalsParams = {
  workspacesWithTypes: WorkspaceWithTypes[];
  componentTypeNames: string[];
  referenceTypeNames: string[];
  availableComponentFields: APIFieldAttributes[];
  tags: APITagAttributes[];
  virtualReferenceTypesByWorkspace: VirtualReferenceTypesByWorkspace;
};

export const toGroupingOptionsForTraversals = ({
  workspacesWithTypes,
  componentTypeNames,
  referenceTypeNames,
  availableComponentFields,
  tags,
  virtualReferenceTypesByWorkspace,
}: ToGroupingOptionsForTraversalsParams) => {
  const transaction = startTransaction(
    'toGroupingOptionsForTraversals',
    500,
    Team.INSIGHT
  );

  const componentTypeNamesSet = new Set(componentTypeNames);
  const referenceTypeNamesSet = new Set(referenceTypeNames);
  const availableComponentFieldNamesSet = new Set(
    availableComponentFields.map(f => f.name)
  );

  const result = {
    appliedGroupingRules: runWithSpan(
      transaction,
      'get applied grouping rules',
      () => groupingRuleInterface.getAll().map(mapToPerspectiveGroupingRule)
    ),
    workspaces: runWithSpan(transaction, 'get workspace options', () =>
      getWorkspaceOptions(workspacesWithTypes)
    ),
    componentTypesPerWorkspace: runWithSpan(
      transaction,
      'get component type options per workspace',
      () =>
        getComponentTypeOptionsPerWorkspace(
          workspacesWithTypes,
          componentTypeNamesSet
        )
    ),
    referenceTypesPerWorkspace: runWithSpan(
      transaction,
      'get reference type options per workspace',
      () =>
        getReferenceTypeOptionsPerWorkspace(
          workspacesWithTypes,
          virtualReferenceTypesByWorkspace,
          referenceTypeNamesSet
        )
    ),
    globalReferenceTypeNames: referenceTypeNames,
    globalComponentTypeNames: componentTypeNames,
    fieldsPerWorkspace: runWithSpan(
      transaction,
      'get component field options per workspace',
      () =>
        getComponentFieldOptionsPerWorkspace(
          workspacesWithTypes,
          availableComponentFieldNamesSet
        )
    ),
    tagsPerWorkspace: runWithSpan(
      transaction,
      'get tag options per workspace',
      () => groupTagOptionsPerWorkspace(tags, workspacesWithTypes)
    ),
  };
  endTransaction(transaction);
  return result;
};

type ValidTagGroupingRule = Required<
  Pick<GroupingRule, 'type' | 'workspaceId' | 'targetId'>
>;
const isValidTagGroupingRule = (
  groupingRule: GroupingRule
): groupingRule is ValidTagGroupingRule => {
  return Boolean(
    groupingRule.type === GroupType.TAG &&
    groupingRule.workspaceId &&
    groupingRule.targetId
  );
};

export const getTagIdsAndWorkspaceIdsOfClosedWorkspacesUsedInGroupingRules = (
  appliedGroupingRules: GroupingRule[],
  workspaceIds: ArdoqId[]
) => {
  const tagIdsAndWorkspaceIdsOfClosedWorkspaces = appliedGroupingRules
    .filter(
      (groupingRule): groupingRule is ValidTagGroupingRule =>
        isValidTagGroupingRule(groupingRule) &&
        !workspaceIds.includes(groupingRule.workspaceId)
    )
    .map(groupingRule => ({
      id: groupingRule.targetId,
      workspaceId: groupingRule.workspaceId,
    }));

  return uniq(tagIdsAndWorkspaceIdsOfClosedWorkspaces);
};

type ValidFieldGroupingRule = Required<
  Pick<GroupingRule, 'type' | 'workspaceId' | 'targetId'>
>;
const isValidFieldGroupingRule = (
  groupingRule: GroupingRule
): groupingRule is ValidFieldGroupingRule => {
  return Boolean(
    groupingRule.type === GroupType.FIELD &&
    groupingRule.workspaceId &&
    groupingRule.targetId
  );
};

const getFieldIdsAndWorkspaceIdsOfClosedWorkspacesUsedInGroupingRules = (
  appliedGroupingRules: GroupingRule[],
  workspaceIds: ArdoqId[]
) => {
  const fieldIdsAndWorkspaceIdsOfClosedWorkspaces = appliedGroupingRules
    .filter(
      (groupingRule): groupingRule is ValidFieldGroupingRule =>
        isValidFieldGroupingRule(groupingRule) &&
        !workspaceIds.includes(groupingRule.workspaceId)
    )
    .map(groupingRule => {
      const field = fieldInterface.get(groupingRule.targetId);
      return {
        id: groupingRule.targetId,
        workspaceId: groupingRule.workspaceId,
        label: field?.label ?? '',
      };
    });

  return uniq(fieldIdsAndWorkspaceIdsOfClosedWorkspaces);
};

export const parseAvailiableTags = (
  tags: APITagAttributes[]
): Array<{ id: string; label: string; workspaceId: string }> => {
  return tags.map(tag => ({
    id: tag._id,
    label: tag.name,
    workspaceId: tag.rootWorkspace,
  }));
};

export const toGroupingOptionsIncludingSavedPerspectiveEntriesFromClosedWorkspaces =
  (
    openWorkspacesWithTypes: WorkspaceWithTypes[],
    allWorkspacesWithTypes: WorkspaceWithTypes[],
    fetchedTags: { id: string; label: string; workspaceId: string }[],
    virtualReferenceTypesByWorkspace: VirtualReferenceTypesByWorkspace
  ): PerspectivesGroupsOptions => {
    const openWorkspaceIds = openWorkspacesWithTypes.map(
      ({ workspace }) => workspace._id
    );
    const appliedGroupingRules: GroupingRule[] = groupingRuleInterface
      .getAll()
      .map(mapToPerspectiveGroupingRule);

    const fieldsOfClosedWorkspaces =
      getFieldIdsAndWorkspaceIdsOfClosedWorkspacesUsedInGroupingRules(
        appliedGroupingRules,
        openWorkspaceIds
      );

    const groupOptionsWithClosedWorkspacesEntities = {
      appliedGroupingRules: appliedGroupingRules.map(
        mapToPerspectiveGroupingRule
      ),
      workspaces: getWorkspaceOptions(allWorkspacesWithTypes),
      componentTypesPerWorkspace: getComponentTypeOptionsPerWorkspace(
        allWorkspacesWithTypes
      ),
      referenceTypesPerWorkspace: getReferenceTypeOptionsPerWorkspace(
        allWorkspacesWithTypes,
        virtualReferenceTypesByWorkspace
      ),
      fieldsPerWorkspace: {
        ...mapFetchedOptionsToGroupOptions(fieldsOfClosedWorkspaces),
        ...getComponentFieldOptionsPerWorkspace(openWorkspacesWithTypes),
      },
      tagsPerWorkspace: {
        ...mapFetchedOptionsToGroupOptions(fetchedTags),
        ...getTagOptionsPerWorkspace(openWorkspacesWithTypes),
      },
      globalComponentTypeNames: [],
      globalReferenceTypeNames: [],
    };

    return groupOptionsWithClosedWorkspacesEntities;
  };

const groupOptionsHaveChanged$ = merge(
  action$.pipe(
    ofType(editorOpened, setActivePerspective, notifyPerspectiveApplied)
  ),
  fields$,
  tags$,
  groupingRules$
);

const perspectiveEditorGroupingOptionsWithoutFieldsChange$ = derivedStream(
  'perspectiveEditorGroupingOptions$',
  context$
);

const virtualReferenceTypesByWorkspace$ = loadedGraph$.pipe(
  map(getVirtualReferenceTypesByWorkspaceFromLoadedGraph),
  distinctUntilChanged(isEqual)
);

const toPerspectiveEditorGroupingOptions = ({
  context,
  viewpointOptionsSourceData,
  workspaceAndModel,
  tags,
  virtualReferenceTypesByWorkspace,
}: {
  context: ContextShape;
  viewpointOptionsSourceData: ViewpointOptionsSourceData | null;
  workspaceAndModel: WorkspaceAndModelLookup;
  tags: APITagAttributes[];
  virtualReferenceTypesByWorkspace: VirtualReferenceTypesByWorkspace;
}): PerspectivesGroupsOptions => {
  if (viewpointOptionsSourceData) {
    const workspacesWithTypes = scopeDataOperations.toWorkspacesWithTypes(
      workspaceAndModel,
      viewpointOptionsSourceData.workspaceIds
    );
    return toGroupingOptionsForTraversals({
      ...viewpointOptionsSourceData,
      workspacesWithTypes,
      virtualReferenceTypesByWorkspace,
    });
  }

  const openWorkspacesWithTypes = scopeDataOperations.toWorkspacesWithTypes(
    workspaceAndModel,
    context.workspaceIds
  );

  if (perspectiveInterface.isSavedPerspectiveSelected()) {
    const appliedGroupingRules: GroupingRule[] = groupingRuleInterface
      .getAll()
      .map(mapToPerspectiveGroupingRule);

    const closedWorkspaceIdsUsedInGroupingRules = uniq(
      appliedGroupingRules
        .map(groupingRule => groupingRule.workspaceId)
        .filter(isTruthy)
        .filter(workspaceId => !context.workspaceIds.includes(workspaceId))
    );

    const allWorkspaceIds = [
      ...context.workspaceIds,
      ...closedWorkspaceIdsUsedInGroupingRules,
    ];

    const allWorkspacesWithTypes = scopeDataOperations.toWorkspacesWithTypes(
      workspaceAndModel,
      allWorkspaceIds
    );

    const fetchedTags = parseAvailiableTags(tags);

    return toGroupingOptionsIncludingSavedPerspectiveEntriesFromClosedWorkspaces(
      openWorkspacesWithTypes,
      allWorkspacesWithTypes,
      fetchedTags,
      virtualReferenceTypesByWorkspace
    );
  }

  return toGroupingOptions(
    openWorkspacesWithTypes,
    virtualReferenceTypesByWorkspace
  );
};

export const perspectiveEditorGroupingOptions$ = combineLatest([
  perspectiveEditorGroupingOptionsWithoutFieldsChange$,
  viewpointOptionsSourceData$,
  groupOptionsHaveChanged$,
  workspaceAndModel$,
  virtualReferenceTypesByWorkspace$,
]).pipe(
  spreadMap(
    (
      [context],
      viewpointOptionsSourceData,
      _,
      workspaceAndModel,
      virtualReferenceTypesByWorkspace
    ) => {
      const tagIds = perspectiveInterface.isSavedPerspectiveSelected()
        ? getTagIdsAndWorkspaceIdsOfClosedWorkspacesUsedInGroupingRules(
            groupingRuleInterface.getAll().map(mapToPerspectiveGroupingRule),
            context.workspaceIds
          ).map(({ id }) => id)
        : [];

      return {
        context,
        viewpointOptionsSourceData,
        workspaceAndModel,
        tagIds,
        virtualReferenceTypesByWorkspace,
      };
    }
  ),
  switchMap(({ tagIds, ...rest }) =>
    from(tagApi.fetchTags(tagIds)).pipe(
      handleError(() => []),
      map(tags => ({ ...rest, tags }))
    )
  ),
  map(toPerspectiveEditorGroupingOptions)
);
